import { Bot } from 'lucide-react';
import { useAiProviders } from '@/api/ai';
import { useAiAgents, useSaveAiAgent } from '@/api/ai-agents';
import type { AiAgent, AiProvider } from '@/api/types';
import { Badge, Card, Checkbox, ErrorState, Skeleton, useToast } from '@/components/ui';
import { AiProvidersSection } from '@/features/settings/AiSettingsPage';
import { AiUsageCard } from '@/features/settings/AiUsageCard';

/**
 * Platform console: manage one client company's AI without entering its workspace — its Claude /
 * Gemini / … connections, which model each of its AI agents answers with, and its token usage.
 * Every call carries the client's workspace id (super admins only).
 */
export function ClientAiManager({
  workspaceId,
  workspaceName,
}: {
  workspaceId: string;
  workspaceName: string;
}) {
  return (
    <div className="mt-8 space-y-6">
      <div>
        <h2 className="font-heading text-lg font-bold text-slate-900">AI for {workspaceName}</h2>
        <p className="text-sm text-slate-500">
          Providers and AI agents belong to this company only — other clients never see or use them.
        </p>
      </div>
      <AiProvidersSection workspaceId={workspaceId} />
      <ClientAiAgents workspaceId={workspaceId} />
      <AiUsageCard workspaceId={workspaceId} className="" />
    </div>
  );
}

function ClientAiAgents({ workspaceId }: { workspaceId: string }) {
  const agents = useAiAgents(workspaceId);
  const providers = useAiProviders(workspaceId);

  return (
    <section>
      <div className="mb-3">
        <h3 className="text-base font-bold">AI agents</h3>
        <p className="text-sm text-slate-500">
          Pick which connection each agent answers with. “Company default” follows the provider marked Default
          above.
        </p>
      </div>
      {agents.isPending ? (
        <Skeleton className="h-28 rounded-2xl" />
      ) : agents.error ? (
        <Card>
          <ErrorState error={agents.error} onRetry={() => void agents.refetch()} />
        </Card>
      ) : agents.data.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-400">
          This client hasn’t created an AI agent yet. Agents (instructions, skills) are set up inside the
          workspace under AI agents.
        </Card>
      ) : (
        <Card className="divide-y divide-slate-100">
          {agents.data.map((a) => (
            <AgentRow key={a.id} agent={a} providers={providers.data ?? []} workspaceId={workspaceId} />
          ))}
        </Card>
      )}
    </section>
  );
}

function AgentRow({
  agent: a,
  providers,
  workspaceId,
}: {
  agent: AiAgent;
  providers: AiProvider[];
  workspaceId: string;
}) {
  const save = useSaveAiAgent(workspaceId);
  const toast = useToast();
  const usable = providers.filter((p) => p.isEnabled);
  const def = usable.find((p) => p.isDefault) ?? usable[0];
  const bound = a.providerId ? providers.find((p) => p.id === a.providerId) : null;
  // A pinned connection that was removed or disabled silently falls back to the default.
  const fallsBack = !!a.providerId && !bound?.isEnabled;
  const answersWith = bound?.isEnabled ? bound : def;

  const update = (input: { providerId?: string | null; isEnabled?: boolean }, done: string) =>
    save.mutate(
      { id: a.id, input },
      { onSuccess: () => toast.success(done), onError: (e) => toast.error(e.message) },
    );

  return (
    <div className="flex flex-wrap items-center gap-4 p-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100">
        <Bot className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-900">
          {a.name}
          {!a.isEnabled && <Badge>Off</Badge>}
          {fallsBack && <Badge tone="amber">Pinned provider unavailable</Badge>}
        </p>
        <p className="truncate text-xs text-slate-500">
          {answersWith ? (
            <>
              Answers with {answersWith.name} · <span className="font-mono">{answersWith.model}</span>
            </>
          ) : (
            'No usable provider — add one above'
          )}
        </p>
      </div>
      <label className="sr-only" htmlFor={`agent-provider-${a.id}`}>
        Provider for {a.name}
      </label>
      <select
        id={`agent-provider-${a.id}`}
        className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-800 sm:w-64"
        value={fallsBack ? '' : (a.providerId ?? '')}
        disabled={save.isPending}
        onChange={(e) => {
          const providerId = e.target.value || null;
          const name = providers.find((p) => p.id === providerId)?.name ?? 'the company default';
          update({ providerId }, `${a.name} now answers with ${name}`);
        }}
      >
        <option value="">Company default{def ? ` (${def.name})` : ''}</option>
        {usable.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} · {p.model}
          </option>
        ))}
      </select>
      <Checkbox
        label="On"
        checked={a.isEnabled}
        disabled={save.isPending}
        onChange={(e) =>
          update({ isEnabled: e.target.checked }, `${a.name} turned ${e.target.checked ? 'on' : 'off'}`)
        }
      />
    </div>
  );
}
